import React, { FC } from 'react';
import { Link } from 'react-router-dom';
import { PageHero, CtaBanner } from '../components/SharedComponents';
import EnquiryForm from '../components/EnquiryForm';
import { ProductCategoryPageProps } from '../types';
import { IMG, WHATSAPP_LINK } from '../data/constants';
import { Phone, MessageCircle, ChevronRight } from 'lucide-react';
import {
  PRODUCTS,
  BARCODE_LABELS_SUBCATS, TTR_RIBBONS_SUBCATS, PRINTERS_SUBCATS,
  SCANNERS_SUBCATS, JEWELLERY_SUBCATS, TAFFETA_SUBCATS, RETAIL_POS_SUBCATS,
} from '../data/products';

const ProductCategoryPage: FC<ProductCategoryPageProps> = ({
  title, subtitle, heroImage, ctaImage, description, features, subcategories, parentPath, parentId,
}) => (
  <>
    <PageHero breadcrumbs={[{ label: 'Home', link: '/' }, { label: 'Barcode Solutions', link: '/products' }, { label: title }]} title={title} subtitle={subtitle} />

    <section style={{ background: 'var(--light)' }}>
      <div className="container">
        <div className="prod-cat-layout">
          {/* LEFT - Main Content */}
          <div className="prod-cat-main">
            <img
              className="prod-hero-img"
              src={heroImage}
              alt={title}
              onError={(e) => { (e.target as HTMLImageElement).style.background = '#e2e8f0'; }}
            />
            <span className="section-tag">About This Product</span>
            <h2 style={{ fontFamily: 'Montserrat', fontWeight: 900, fontSize: 28, color: 'var(--primary)', marginBottom: 14 }}>{title}</h2>
            <p className="prod-cat-desc">{description}</p>
            <ul className="check-list">
              {features.map((f, i) => <li key={i}>{f}</li>)}
            </ul>

            {/* SUBCATEGORIES */}
            <h3 style={{ fontFamily: 'Montserrat', fontWeight: 800, fontSize: 22, color: 'var(--primary)', marginBottom: 8, marginTop: 40 }}>
              Available Variants &amp; Sub-Categories
            </h3>
            <p style={{ color: 'var(--gray)', fontSize: 14, marginBottom: 24 }}>
              Click on any variant below to know more or use the enquiry form to get pricing.
            </p>
            <div className="subcat-grid">
              {subcategories.map(sc => (
                <Link
                  key={sc.slug}
                  to={`/${parentId}/${sc.slug}`}
                  className="subcat-card subcat-card-link"
                >
                  <img
                    className="subcat-img"
                    src={sc.image}
                    alt={sc.name}
                    onError={(e) => { (e.target as HTMLImageElement).style.background = '#e2e8f0'; }}
                  />
                  <div className="subcat-body">
                    <h4>{sc.name}</h4>
                    <p>{sc.description}</p>
                    {sc.features && (
                      <div className="subcat-feats">
                        {sc.features.map(f => <span key={f} className="subcat-feat-tag">{f}</span>)}
                      </div>
                    )}
                    <div className="subcat-cta-row">
                      <span className="subcat-view-btn">View Details <ChevronRight size={13} /></span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>

            {/* Related Products */}
            <div style={{ marginTop: 40, padding: 24, background: '#fff', borderRadius: 12, border: '1px solid var(--border)' }}>
              <h3 style={{ fontFamily: 'Montserrat', fontWeight: 800, fontSize: 18, color: 'var(--primary)', marginBottom: 16 }}>You Might Also Need</h3>
              <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                {PRODUCTS.filter(p => p.title !== title).slice(0, 4).map(p => (
                  <Link key={p.id} to={p.path} style={{ background: 'rgba(230,48,39,.07)', color: 'var(--accent)', fontFamily: 'Montserrat', fontWeight: 700, fontSize: 13, padding: '8px 16px', borderRadius: 8, textDecoration: 'none', border: '1px solid rgba(230,48,39,.2)', transition: 'all .2s' }}>
                    {p.title}
                  </Link>
                ))}
              </div>
            </div>
          </div>

          {/* RIGHT - Sticky Enquiry Form */}
          <div className="side-enquiry">
            <EnquiryForm productName={title} />
            <div style={{ background: 'var(--primary)', borderRadius: 12, padding: 22, marginTop: 16, color: '#fff' }}>
              <p style={{ fontFamily: 'Montserrat', fontWeight: 700, fontSize: 13, marginBottom: 12, display: 'flex', alignItems: 'center', gap: 6 }}>
                <Phone size={14} /> Call Us Directly
              </p>
              <a href="tel:+919319299973" style={{ color: 'var(--accent2)', fontWeight: 700, fontSize: 15, display: 'block', marginBottom: 6, textDecoration: 'none' }}>+91 9319299973</a>
              <a href="tel:+919319299974" style={{ color: 'var(--accent2)', fontWeight: 700, fontSize: 15, display: 'block', marginBottom: 14, textDecoration: 'none' }}>+91 9319299974</a>
              <p style={{ fontSize: 12, color: '#64748b' }}>Mon–Sat: 9:30 AM – 6:30 PM</p>
              <p style={{ fontSize: 12, color: '#64748b', marginTop: 4 }}>Dehradun, Uttarakhand</p>
              <a href={WHATSAPP_LINK} target="_blank" rel="noreferrer"
                style={{ display: 'flex', alignItems: 'center', gap: 8, background: '#25d366', color: '#fff', fontFamily: 'Montserrat', fontWeight: 700, fontSize: 13, padding: '10px 16px', borderRadius: 8, textDecoration: 'none', marginTop: 14, justifyContent: 'center' }}>
                <MessageCircle size={16} /> WhatsApp Us
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>

    <CtaBanner image={ctaImage} title={`Need Custom ${title}?`} subtitle="Any size, material or quantity — bulk pricing available."
      primaryBtnText="Get Quote →" primaryBtnLink="/contact"
      secondaryBtnText="WhatsApp Us" secondaryBtnLink={WHATSAPP_LINK} secondaryIsExternal />
  </>
);

// ── 7 Product Pages ────────────────────────────────────────────────────────
export const BarcodeLabels: FC = () => <ProductCategoryPage
  title="Barcode Labels" subtitle="Custom thermal and laser barcode labels in rolls and sheets — all sizes, materials and adhesives."
  heroImage={IMG.barcodeLabels} ctaImage={IMG.ctaLabels} parentId="barcode-labels" parentPath="/barcode-labels"
  description="We supply a complete range of barcode label rolls and sheets in thermal direct, thermal transfer, polyester, polypropylene and paper materials. All sizes, adhesive types and materials available. Compatible with all major barcode printer brands including Zebra, Honeywell and TSC."
  features={['Available in all standard and custom sizes', 'Compatible with Zebra, Honeywell, TSC printers', 'Sizes: 25mm, 40mm, 50mm, 75mm, 100mm+', 'Materials: Paper, Polyester, Polypropylene, PVC', 'Adhesives: Permanent, Removable, Freezer-grade', 'Minimum order: 1 roll', 'Pan-India fast delivery']}
  subcategories={BARCODE_LABELS_SUBCATS}
/>;

export const TtrRibbons: FC = () => <ProductCategoryPage
  title="Thermal Transfer Ribbons" subtitle="High-quality wax, wax-resin and full resin TTR ribbons compatible with all major printer brands."
  heroImage={IMG.ttrRibbons} ctaImage={IMG.ctaRibbons} parentId="ttr-ribbons" parentPath="/ttr-ribbons"
  description="We supply high-quality thermal transfer ribbons in wax, wax-resin and full resin formulations. Compatible with Zebra, Honeywell, TSC, Datalogic and all major barcode printer brands. Available in all widths and lengths."
  features={['Compatible with Zebra, Honeywell, TSC, Datalogic', 'Widths: 40mm, 50mm, 60mm, 80mm, 110mm', 'Standard lengths: 300m, 450m, 600m', 'Wax for paper labels, Resin for polyester', 'Excellent smudge and scratch resistance', 'Bulk quantities at discounted rates']}
  subcategories={TTR_RIBBONS_SUBCATS}
/>;

export const Printers: FC = () => <ProductCategoryPage
  title="Barcode Printers" subtitle="Desktop, industrial and mobile barcode label printers from top global brands."
  heroImage={IMG.printers} ctaImage={IMG.ctaPrinter} parentId="printers" parentPath="/printers"
  description="We are authorized dealers for Zebra Technologies, Honeywell and TSC barcode printers. We supply desktop, industrial and mobile barcode label printers for all applications. All printers come with full warranty, genuine consumables and expert technical support."
  features={['Authorized Zebra, Honeywell & TSC dealer', 'Desktop printers for office & light duty use', 'Industrial printers for 24/7 warehouse use', 'Full warranty with genuine spare parts', 'Free driver installation and setup support', 'AMC plans available for all printer models']}
  subcategories={PRINTERS_SUBCATS}
/>;

export const Scanners: FC = () => <ProductCategoryPage
  title="Barcode Scanners" subtitle="1D and 2D barcode scanners — handheld, wireless and presentation scanners for every need."
  heroImage={IMG.scanners} ctaImage={IMG.ctaScanner} parentId="scanners" parentPath="/scanners"
  description="We supply a complete range of 1D and 2D barcode scanners for retail, warehousing and industrial use. Our range includes handheld wired, wireless Bluetooth, presentation and industrial ruggedized scanners from top brands like Honeywell, Datalogic and Zebra."
  features={['1D laser and 2D imager options available', 'Wired USB, Wireless Bluetooth & RF options', 'Compatible with all major POS and ERP systems', 'Plug & Play — no driver needed', 'IP54/IP65 ruggedized models available', 'Full warranty and after-sales service']}
  subcategories={SCANNERS_SUBCATS}
/>;

export const JewelleryLabels: FC = () => <ProductCategoryPage
  title="Jewellery Labels" subtitle="Specialized barcode price tags and labels for gold, silver and jewellery retail stores."
  heroImage={IMG.jewelleryLabels} ctaImage={IMG.ctaJewellery} parentId="jewellery-labels" parentPath="/jewellery-labels"
  description="We supply specialized barcode price tags and labels for jewellery stores. Our jewellery labels are compatible with all major jewellery management software and barcode printers. Available in various sizes for gold, silver and diamond items."
  features={['Compatible with all jewellery billing software', 'Sizes: 1x0.5 inch, 2x1 inch and custom sizes', 'String tags and self-adhesive options', 'Compatible with Zebra and TSC printers', 'Minimum order: 1 roll or 1000 tags', 'Custom branding and design available']}
  subcategories={JEWELLERY_SUBCATS}
/>;

export const TaffetaLabels: FC = () => <ProductCategoryPage
  title="Taffeta Labels" subtitle="Woven and printed taffeta labels for garments, textiles and fashion brands."
  heroImage={IMG.taffetaLabels} ctaImage={IMG.ctaTaffeta} parentId="taffeta-labels" parentPath="/taffeta-labels"
  description="We supply high-quality woven and printed taffeta labels for garment manufacturers, fashion brands and textile companies. Custom designs, colors and sizes are available with your brand logo, care instructions and size information."
  features={['Custom woven and printed designs available', 'Brand logo, care instructions & size info', 'Soft, durable and machine-washable', 'Minimum order: 500 labels', 'Satin, taffeta and nylon materials available', 'Folded and straight cut options available']}
  subcategories={TAFFETA_SUBCATS}
/>;

export const RetailPOS: FC = () => <ProductCategoryPage
  title="Retail POS / Billing" subtitle="Complete point-of-sale systems with billing software, receipt printers and cash drawers."
  heroImage={IMG.retailPOS} ctaImage={IMG.ctaPOS} parentId="retail-pos" parentPath="/retail-pos"
  description="We supply complete retail POS systems for shops, supermarkets, jewellery stores and all retail businesses. Our POS solutions include billing software, receipt printers, barcode scanners, cash drawers with complete installation and staff training."
  features={['GST-compliant billing software included', 'Complete hardware + software setup', 'Inventory management with barcode scanning', 'Sales reports and analytics dashboard', 'Cloud backup and data security', 'On-site installation and staff training']}
  subcategories={RETAIL_POS_SUBCATS}
/>;

export default ProductCategoryPage;
